import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { Spinner } from 'components/Spinner'

import { CANCEL_EXTRA, UNSUBSCRIBE_SECONDARY } from 'root-redux/actions/user'
import {
  selectActionList,
  selectAppName,
  selectSource,
} from 'root-redux/selects/common'
import {
  selectUUID,
  selectUserCohort,
  selectUserSecondariesSubscriptionInfo,
  selectUserSubscriptionInfo,
} from 'root-redux/selects/user'

import { useCompatibleRetentionFlowPaymentMethod } from 'hooks/useCompatibleRetentionFlowPaymentMethod'
import { useIsWithdrawalEligible } from 'hooks/useIsWithdrawalEligible'

import { getSubscriptionUIStatus } from 'helpers/getSubscriptionUIStatus'

import { ExtraSubscription } from 'modules/unsubscribe/components/Subscriptions/ExtraSubscription'
import { InAppPurchases } from 'modules/unsubscribe/components/Subscriptions/InAppPurchases'
import { MainSubscriptionRows } from 'modules/unsubscribe/components/Subscriptions/MainSubscriptionRows'
import { SecondarySubscription } from 'modules/unsubscribe/components/Subscriptions/SecondarySubscription'
import { TurnOffRenewalButton } from 'modules/unsubscribe/components/Subscriptions/components/TurnOffRenewalButton'
import { WithdrawalButton } from 'modules/unsubscribe/components/Subscriptions/components/WithdrawalButton'
import { getContractLabel } from 'modules/unsubscribe/helpers'

import { eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { SubscriptionContainer as Container } from 'common-styles'
import { PageId } from 'page-constants'
import {
  AppName,
  EVENT_SOURCE,
  SubscriptionType,
  WELLHUB_COHORT,
} from 'root-constants'

import { FemiColor } from '../../constants'
import { StyledFemiSubscriptions as S } from './FemiSubscriptions.styles'

const WITHDRAWAL_BUTTON_STYLE = {
  width: '100%',
  maxWidth: 'none',
  height: '56px',
  margin: '12px 0 0',
  borderRadius: '16px',
  backgroundColor: FemiColor.GRAY_LIGHT,
  color: FemiColor.DARK,
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
}

type TProps = {
  onRequestWithdrawal: (subscriptionId: string, contract: string) => void
}

export const FemiSubscriptions: React.FC<TProps> = ({
  onRequestWithdrawal,
}) => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const appName = useSelector(selectAppName) as AppName
  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const userSecondarySubscriptions = useSelector(
    selectUserSecondariesSubscriptionInfo,
  )
  const userCohort = useSelector(selectUserCohort)
  const source = useSelector(selectSource)
  const userId = useSelector(selectUUID)
  const fetchingActionsList = useSelector(selectActionList)
  const { isCanceledSubscription, isActiveSubscription, isPausedSubscription } =
    getSubscriptionUIStatus(userSubscriptionInfo?.uiStatus)

  const isCompatibleRetentionFlowPaymentMethod =
    useCompatibleRetentionFlowPaymentMethod()
  const showWithdrawalFlow = useIsWithdrawalEligible()

  const isCancelSubscriptionInProgress =
    fetchingActionsList.includes(CANCEL_EXTRA) ||
    fetchingActionsList.includes(UNSUBSCRIBE_SECONDARY)

  const isExternalUser = userCohort === WELLHUB_COHORT

  const cancelSubscription = () => {
    eventLogger.logTurnOffRenewalButtonTap({
      source,
      subscriptionType: SubscriptionType.SUBSCRIPTION,
      userId,
    })

    goTo({ pathname: PageId.REASONS, search })
  }

  const subscribeAgain = () => {
    eventLogger.logContactSupportClicked(EVENT_SOURCE.ACCOUNT_PROFILE, userId)

    window.open('/contact-form', '_blank', 'noopener,noreferrer')
  }

  if (!userSubscriptionInfo) {
    return null
  }

  return isCancelSubscriptionInProgress ? (
    <Spinner />
  ) : (
    <S.Wrapper>
      <Container.Root isBottomSeparatorHidden={isCanceledSubscription}>
        <Container.CardTitle>
          {t('subscription.yourPlan', { context: appName })}
        </Container.CardTitle>

        <MainSubscriptionRows />

        {isActiveSubscription && !isExternalUser && (
          <S.ActionButton type="button" onClick={cancelSubscription}>
            {t('retention.femi.cancelSubscription')}
          </S.ActionButton>
        )}
        {isPausedSubscription && (
          <S.ActionButton type="button" onClick={subscribeAgain}>
            {t('subscription.subscribeAgain')}
          </S.ActionButton>
        )}
        {showWithdrawalFlow && !isCanceledSubscription && (
          <WithdrawalButton
            subscriptionId={userSubscriptionInfo.subscriptionId}
            contract={getContractLabel(
              appName,
              userSubscriptionInfo.interval,
              userSubscriptionInfo.intervalCount,
            )}
            onRequestWithdrawal={onRequestWithdrawal}
            style={WITHDRAWAL_BUTTON_STYLE}
          />
        )}
        {isPausedSubscription &&
          isCompatibleRetentionFlowPaymentMethod &&
          !isExternalUser && (
            <S.SecondaryAction>
              <TurnOffRenewalButton
                onClick={cancelSubscription}
                isMainSubscription
              />
            </S.SecondaryAction>
          )}
      </Container.Root>

      {userSecondarySubscriptions.map((subscription) => (
        <SecondarySubscription
          key={subscription.subscriptionId}
          secondarySubscription={subscription}
          onRequestWithdrawal={onRequestWithdrawal}
        />
      ))}

      {userSubscriptionInfo.extras?.map((extra) => (
        <ExtraSubscription key={extra.id} extra={extra} />
      ))}
      <InAppPurchases />
    </S.Wrapper>
  )
}
